/**
 * Cart drawer upsell carousel + free shipping progress bar.
 *
 * Uses event delegation on `document` for the "+" buttons and the popover
 * controls so clicks keep working even after the cart drawer's markup is
 * replaced wholesale by the Section Rendering API (a `<script type="module">`
 * with a src that has already loaded does not re-execute just because a
 * fresh copy of the tag is reinserted into the DOM, so per-element binding
 * would silently stop working after the first add-to-cart swap — delegation
 * sidesteps that entirely).
 *
 * Behavior:
 *  - Each upsell card's "+" button no longer adds to the cart directly. It
 *    opens a small popover for that product with size/color pickers (built
 *    from the product's variants, embedded as JSON in the card's popover
 *    markup) and its own "Add to cart" button.
 *  - Selecting options resolves the matching variant, updates the
 *    displayed price, and disables/marks combinations that don't resolve to
 *    an available variant.
 *  - The popover's own "Add to cart" button is the only thing that posts to
 *    /cart/add.js. On success it refreshes the cart drawer sections and the
 *    header cart badge, matching the AJAX pattern used elsewhere in the
 *    custom storefront additions.
 *  - The free shipping progress bar re-reads the cart total after any cart
 *    change and updates its fill width + message live, without a full page
 *    reload.
 */
(function () {
  if (window.__cartDrawerUpsellInit) return;
  window.__cartDrawerUpsellInit = true;

  var FREE_SHIPPING_THRESHOLD_CENTS = 30000;

  function formatMoney(cents) {
    return '$' + (cents / 100).toFixed(2);
  }

  function updateShippingProgress(cartTotalCents) {
    document.querySelectorAll('[data-shipping-progress]').forEach(function (el) {
      var threshold = parseInt(el.getAttribute('data-threshold-cents'), 10) || FREE_SHIPPING_THRESHOLD_CENTS;
      var remaining = threshold - cartTotalCents;
      if (remaining < 0) remaining = 0;
      var percent = (cartTotalCents / threshold) * 100;
      if (percent > 100) percent = 100;
      if (percent < 0) percent = 0;

      var fill = el.querySelector('[data-shipping-progress-fill]');
      var message = el.querySelector('[data-shipping-progress-message]');
      var unlocked = cartTotalCents >= threshold;

      if (fill) {
        fill.style.width = percent + '%';
        fill.classList.toggle('is-complete', unlocked);
      }

      if (message) {
        if (unlocked) {
          message.innerHTML =
            '<span class="cart-drawer__shipping-progress-icon" aria-hidden="true">' +
            '<svg width="14" height="14" viewBox="0 0 16 16" fill="none"><path d="M3 8.5l3 3 7-7" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/></svg>' +
            '</span> You\'ve unlocked free shipping!';
        } else {
          message.innerHTML =
            'You\'re <strong data-shipping-progress-remaining>' + formatMoney(remaining) + '</strong> away from free shipping';
        }
      }
    });
  }

  function refreshCartBadge(itemCount) {
    document.querySelectorAll('[data-cart-count]').forEach(function (countEl) {
      countEl.textContent = itemCount;
      countEl.classList.toggle('custom-mega-header__cart-count--hidden', itemCount === 0);
    });

    var cartIcon = document.querySelector('cart-icon');
    if (cartIcon) {
      if (typeof cartIcon.renderCartBubble === 'function') {
        cartIcon.renderCartBubble(itemCount);
      } else {
        var countTextEl = cartIcon.querySelector('.cart-bubble__text-count');
        var bubbleEl = cartIcon.querySelector('.cart-bubble');
        if (countTextEl) {
          countTextEl.textContent = itemCount < 100 ? String(itemCount) : '';
          countTextEl.classList.toggle('hidden', itemCount === 0);
        }
        if (bubbleEl) bubbleEl.classList.toggle('visually-hidden', itemCount === 0);
      }
    }

    var themeBadge = document.querySelector('.theme-drawer__badge');
    if (themeBadge) {
      themeBadge.textContent = itemCount;
      themeBadge.classList.toggle('visually-hidden', itemCount === 0);
    }
  }

  function refreshDrawerSections(sectionsHtml) {
    if (!sectionsHtml) return;
    Object.keys(sectionsHtml).forEach(function (sectionId) {
      var target = document.getElementById('shopify-section-' + sectionId);
      if (!target) return;
      var parsed = new DOMParser().parseFromString(sectionsHtml[sectionId], 'text/html');
      var replacement = parsed.getElementById('shopify-section-' + sectionId) || parsed.body.firstElementChild;
      if (replacement) target.replaceWith(replacement);
    });
  }

  function refreshCartState() {
    fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) {
        return r.json();
      })
      .then(function (cart) {
        refreshCartBadge(cart.item_count);
        updateShippingProgress(cart.total_price);
      })
      .catch(function () {});
  }

  /* ---------------- Popover open/close ---------------- */

  function getBackdrop() {
    return document.querySelector('[data-cart-upsell-backdrop]');
  }

  function closeAllPopovers() {
    document.querySelectorAll('[data-cart-upsell-popover].is-open').forEach(function (popover) {
      closePopover(popover);
    });
  }

  function openPopover(popover, triggerButton) {
    closeAllPopovers();

    popover.hidden = false;
    // Force layout so the transition from the hidden state actually runs.
    void popover.offsetHeight;
    popover.classList.add('is-open');

    var backdrop = getBackdrop();
    if (backdrop) {
      backdrop.hidden = false;
      void backdrop.offsetHeight;
      backdrop.classList.add('is-visible');
    }

    if (triggerButton) triggerButton.setAttribute('aria-expanded', 'true');
    popover.dataset.triggerId = triggerButton && triggerButton.id ? triggerButton.id : '';
  }

  function closePopover(popover) {
    popover.classList.remove('is-open');

    var openPopovers = document.querySelectorAll('[data-cart-upsell-popover].is-open');
    if (openPopovers.length === 0) {
      var backdrop = getBackdrop();
      if (backdrop) backdrop.classList.remove('is-visible');
    }

    document.querySelectorAll('[data-cart-upsell-add][aria-expanded="true"]').forEach(function (btn) {
      if (btn.getAttribute('data-popover-target') === popover.id) {
        btn.setAttribute('aria-expanded', 'false');
      }
    });

    window.setTimeout(function () {
      if (!popover.classList.contains('is-open')) {
        popover.hidden = true;
      }
      var stillOpen = document.querySelectorAll('[data-cart-upsell-popover].is-open');
      if (stillOpen.length === 0) {
        var backdropEl = getBackdrop();
        if (backdropEl && !backdropEl.classList.contains('is-visible')) backdropEl.hidden = true;
      }
    }, 220);
  }

  /* ---------------- Variant resolution ---------------- */

  function getVariantsForPopover(popover) {
    var script = popover.querySelector('[data-popover-variants]');
    if (!script) return [];
    try {
      return JSON.parse(script.textContent) || [];
    } catch (e) {
      return [];
    }
  }

  function getSelectedOptionValues(popover) {
    var values = [];
    popover.querySelectorAll('[data-option-value].is-selected').forEach(function (btn) {
      var position = parseInt(btn.getAttribute('data-option-position'), 10);
      values[position - 1] = btn.getAttribute('data-value');
    });
    return values;
  }

  function findMatchingVariant(popover) {
    var variants = getVariantsForPopover(popover);
    var selected = getSelectedOptionValues(popover);

    for (var i = 0; i < variants.length; i++) {
      var variant = variants[i];
      var options = variant.options || [];
      var matches = true;
      for (var j = 0; j < selected.length; j++) {
        if (options[j] !== selected[j]) {
          matches = false;
          break;
        }
      }
      if (matches) return variant;
    }
    return null;
  }

  function updatePopoverForSelection(popover) {
    var variant = findMatchingVariant(popover);
    var submitBtn = popover.querySelector('[data-popover-add-to-cart]');
    var submitLabel = popover.querySelector('[data-popover-submit-label]');
    var priceEl = popover.querySelector('[data-popover-price]');

    if (variant) {
      popover.dataset.selectedVariantId = variant.id;
      if (priceEl && typeof variant.price === 'number') {
        priceEl.textContent = formatMoney(variant.price);
      }
      if (submitBtn) submitBtn.disabled = !variant.available;
      if (submitLabel) submitLabel.textContent = variant.available ? 'Add to cart' : 'Sold out';
    } else {
      popover.dataset.selectedVariantId = '';
      if (submitBtn) submitBtn.disabled = true;
      if (submitLabel) submitLabel.textContent = 'Select options';
    }
  }

  function handleOptionValueClick(button) {
    var popover = button.closest('[data-cart-upsell-popover]');
    if (!popover) return;

    var groupWrap = button.closest('.cart-drawer__upsell-popover-option');
    if (groupWrap) {
      groupWrap.querySelectorAll('[data-option-value]').forEach(function (btn) {
        btn.classList.remove('is-selected');
        btn.setAttribute('aria-pressed', 'false');
      });
    }
    button.classList.add('is-selected');
    button.setAttribute('aria-pressed', 'true');

    updatePopoverForSelection(popover);
  }

  function handlePopoverAddToCart(button) {
    if (button.disabled || button.classList.contains('is-loading')) return;

    var popover = button.closest('[data-cart-upsell-popover]');
    if (!popover) return;

    var variantId = popover.dataset.selectedVariantId;
    if (!variantId) return;

    button.classList.add('is-loading');
    var label = popover.querySelector('[data-popover-submit-label]');
    var originalLabel = label ? label.textContent : 'Add to cart';
    if (label) label.textContent = 'Adding…';

    var sectionIds = [];
    document.querySelectorAll('cart-items-component[data-section-id]').forEach(function (el) {
      sectionIds.push(el.dataset.sectionId);
    });
    var cartDrawerSectionEl = document.getElementById('shopify-section-cart-drawer-section');
    if (cartDrawerSectionEl) sectionIds.push('cart-drawer-section');

    var body = { id: variantId, quantity: 1 };
    if (sectionIds.length) body.sections = sectionIds.join(',');

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
      body: JSON.stringify(body)
    })
      .then(function (res) {
        return res.json().then(function (data) {
          return { ok: res.ok, data: data };
        });
      })
      .then(function (result) {
        button.classList.remove('is-loading');

        if (!result.ok) {
          if (label) label.textContent = originalLabel;
          return;
        }

        if (label) label.textContent = 'Added ✓';

        refreshDrawerSections(result.data.sections);
        refreshCartState();

        var drawer = document.getElementById('cart-drawer');
        if (drawer && typeof drawer.open === 'function' && !drawer.hasAttribute('open')) {
          drawer.open();
        }

        window.setTimeout(function () {
          closePopover(popover);
        }, 450);
      })
      .catch(function () {
        button.classList.remove('is-loading');
        if (label) label.textContent = originalLabel;
      });
  }

  /* ---------------- Event delegation ---------------- */

  document.addEventListener('click', function (event) {
    var addButton = event.target.closest('[data-cart-upsell-add]');
    if (addButton) {
      if (addButton.disabled) return;
      var targetId = addButton.getAttribute('data-popover-target');
      var popover = targetId ? document.getElementById(targetId) : null;
      if (popover) {
        addButton.id = addButton.id || targetId + '-trigger';
        openPopover(popover, addButton);
        updatePopoverForSelection(popover);
      }
      return;
    }

    var optionValueButton = event.target.closest('[data-option-value]');
    if (optionValueButton) {
      handleOptionValueClick(optionValueButton);
      return;
    }

    var submitButton = event.target.closest('[data-popover-add-to-cart]');
    if (submitButton) {
      handlePopoverAddToCart(submitButton);
      return;
    }

    var closeButton = event.target.closest('[data-popover-close]');
    if (closeButton) {
      var closingPopover = closeButton.closest('[data-cart-upsell-popover]');
      if (closingPopover) closePopover(closingPopover);
      return;
    }

    var backdrop = event.target.closest('[data-cart-upsell-backdrop]');
    if (backdrop) {
      closeAllPopovers();
      return;
    }
  });

  document.addEventListener('keydown', function (event) {
    if (event.key === 'Escape') {
      closeAllPopovers();
    }
  });

  // Refresh the progress bar's numbers whenever the drawer section (or any
  // cart-related section) gets swapped in, since a fresh copy of the
  // progress bar markup carries the server-rendered values from the moment
  // of that specific request, not the very latest cart state after this add
  // resolves elsewhere on the page.
  var observeTarget = document.body;
  if (observeTarget && window.MutationObserver) {
    var debounceHandle = null;
    var observer = new MutationObserver(function () {
      if (debounceHandle) return;
      debounceHandle = setTimeout(function () {
        debounceHandle = null;
        refreshCartState();
      }, 150);
    });
    observer.observe(observeTarget, { childList: true, subtree: true });
  }

  refreshCartState();
})();
